// Fragment fetching & widget render helpers
// Client side replacement for server-side includes (SSI)
// Fetches HTML fragment from URL and appends to target element
// Ensures any <script> tags are executed

export async function fetchFragment(
  url: string,
  options: {
    targetElement: Element;
    /** Allow callers to mutate or replace the list of root elements before injection. */
    preprocess?: (elements: Element[], rawHtml: string) => void | Element[];
    onSuccess?: (html: string) => void;
    onError?: (err: unknown) => void;
  }
) {
  const { targetElement, preprocess, onSuccess, onError } = options;

  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    const html = await response.text();

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const children = Array.from(tempDiv.children);

    let processed = children.map((child) => {
      if (child.tagName === 'SCRIPT') {
        const newScript = document.createElement('script');
        Array.from(child.attributes).forEach((attr) => {
          if (attr.name !== 'nonce') newScript.setAttribute(attr.name, attr.value);
        });
        newScript.textContent = (child as HTMLScriptElement).innerHTML;
        return newScript;
      }
      return child.cloneNode(true) as Element;
    });

    // Allow optional preprocessing / transformation
    try {
      const maybe = preprocess?.(processed, html);
      if (maybe && Array.isArray(maybe)) processed = maybe;
    } catch (preErr) {
      console.warn('fetchFragment preprocess threw an error; continuing with unmodified elements.', preErr);
    }

    processed.forEach((el) => targetElement.appendChild(el));
    onSuccess?.(html);
    return html;
  } catch (error) {
    console.error(`Error fetching/injecting HTML from ${url}:`, error);
    onError?.(error);
    throw error;
  }
}
